import { useState } from "react";
import { Plus, Trash2, ArrowDownLeft, ArrowUpRight, Scale } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, useApi, errMsg } from "@/pos/lib/api";
import { rm, dt, todayStr } from "@/pos/lib/format";
import { Page, PageHeader, Stat, Empty } from "@/pos/components/Common";
import DateRange from "@/pos/components/DateRange";
import Field from "@/pos/components/Field";

const CATS = {
  in: ["Modal Tambahan", "Jualan Luar Sistem", "Lain-lain"],
  out: ["Bayar Pembekal", "Ais & Plastik", "Sewa Gerai", "Gaji Pekerja", "Minyak & Pengangkutan", "Utiliti", "Lain-lain"],
};

function EntryDialog({ open, onClose, onSaved }) {
  const [type, setType] = useState("out");
  const [category, setCategory] = useState(CATS.out[0]);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const save = async () => {
    try {
      await api.post("/cash", { type, category, amount: +amount, note });
      toast.success("Rekod disimpan");
      setAmount(""); setNote("");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cash-entry-dialog">
        <DialogHeader><DialogTitle className="font-heading">Rekod Duit Masuk / Keluar</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {[["in", "Duit Masuk"], ["out", "Duit Keluar"]].map(([t, l]) => (
            <button key={t} onClick={() => { setType(t); setCategory(CATS[t][0]); }} data-testid={`cash-type-${t}`}
              className={`rounded-lg border-2 py-2.5 text-sm font-semibold ${type === t ? (t === "in" ? "border-green-600 bg-green-50 text-green-700" : "border-rose-600 bg-rose-50 text-rose-600") : "border-slate-200"}`}>{l}</button>
          ))}
        </div>
        <Field label="Kategori">
          <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="cash-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS[type].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="Amaun (RM)"><Input type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 font-mono text-lg" data-testid="cash-amount-input" /></Field>
        <Field label="Catatan"><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="cash-note-input" /></Field>
        <button onClick={save} disabled={!(+amount > 0)} className="btn-primary h-11 w-full disabled:opacity-40" data-testid="cash-save-btn">Simpan</button>
      </DialogContent>
    </Dialog>
  );
}

export default function Cashbook() {
  const [range, setRange] = useState([todayStr(), todayStr()]);
  const { data, reload } = useApi("/cash", { date_from: range[0], date_to: range[1] });
  const [open, setOpen] = useState(false);
  const del = async (id) => {
    if (!window.confirm("Padam rekod ini?")) return;
    await api.delete(`/cash/${id}`);
    reload();
  };
  const rows = data?.entries || [];
  return (
    <Page>
      <PageHeader title="Buku Tunai" subtitle="Rekod duit masuk & keluar">
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="cashbook-add-entry-btn"><Plus size={16} /> Rekod Baru</button>
      </PageHeader>
      <div className="mb-4"><DateRange value={range} onChange={setRange} /></div>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="cashbook-tab">
        <Stat label="Duit Masuk" value={rm(data?.total_in)} tone="green" icon={ArrowDownLeft} testid="cash-total-in" />
        <Stat label="Duit Keluar" value={rm(data?.total_out)} tone="rose" icon={ArrowUpRight} testid="cash-total-out" />
        <Stat label="Baki Bersih" value={rm(data?.net)} tone={data?.net < 0 ? "rose" : "sky"} icon={Scale} testid="cash-net" />
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="data-table" data-testid="cash-table">
          <thead><tr><th>Tarikh</th><th>Kategori</th><th>Catatan</th><th>Oleh</th><th className="text-right">Masuk</th><th className="text-right">Keluar</th><th></th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id}>
              <td className="whitespace-nowrap text-slate-500">{dt(r.created_at)}</td>
              <td><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.type === "in" ? "bg-green-50 text-green-700" : "bg-rose-50 text-rose-600"}`}>{r.category}</span></td>
              <td>{r.note}</td>
              <td className="text-slate-500">{r.by}</td>
              <td className="text-right font-mono text-green-700">{r.type === "in" ? rm(r.amount) : ""}</td>
              <td className="text-right font-mono text-rose-600">{r.type === "out" ? rm(r.amount) : ""}</td>
              <td className="text-right">{r.source === "manual" && <button className="icon-btn hover:text-rose-600" onClick={() => del(r.id)} data-testid={`cash-delete-${r.id}`}><Trash2 size={15} /></button>}</td>
            </tr>
          ))}</tbody>
        </table>
        {rows.length === 0 && <Empty text="Tiada rekod dalam tempoh ini" />}
      </div>
      <EntryDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); reload(); }} />
    </Page>
  );
}
